import { isString, isEmpty, isNil, trim } from 'lodash';

type StrIsh = { toString: () => string };
type NonEmptyString = string & { 0: string };

/**
 * Compares two objects that implement the `toString` method for equality, ignoring case.
 *
 * @param a - The first object to compare.
 * @param b - The second object to compare.
 * @returns {boolean} Returns `true` if the string representations of both objects are equal.
 */
export function iEqual(a: StrIsh, b: StrIsh): boolean {
  return a.toString().toLowerCase() === b.toString().toLowerCase();
}

/**
 * Compares two alphanumeric strings for sorting purposes.
 *
 * @param strA - The first alphanumeric string to compare.
 * @param strB - The second alphanumeric string to compare.
 * @returns - Returns -1 if strA < strB, 1 if strA > strB, and 0 if they are equal.
 */
export function compareAlphaNumeric(strA: string, strB: string): number {
  const toChunks = (str: string): string[] => str.match(/[a-zA-Z]+|[0-9]+/gu) || [];
  const toValue = (str: string): number | string => (isNaN(Number(str)) ? str : Number(str));
  const compareChunk = (chunkA: string[], chunkB: string[]): number => {
    const len = Math.min(chunkA.length, chunkB.length);
    for (let i = 0; i < len; i++) {
      const a = toValue(chunkA.at(i) ?? '');
      const b = toValue(chunkB.at(i) ?? '');
      if (a === b) {
        // eslint-disable-next-line no-continue
        continue;
      }
      return a > b ? 1 : -1;
    }
    return chunkA.length - chunkB.length;
  };
  return compareChunk(toChunks(strA), toChunks(strB));
}

export function extractTextFromHtml(html: string): string {
  html = html.replace(/<(?<temp1>script|style|iframe|object)[^>]*>[\s\S]*?<\/\1>/giu, '');

  let previous: string;
  do {
    previous = html;
    html = html.replace(/<[^>]*>/gu, '');
  } while (html !== previous);

  return html.replace(/\s+/gu, ' ').trim();
}

export function toUpperCaseAlphaNumericOnly(content?: string): string {
  const nonAlphaNumeric = /[^a-zA-Z0-9]/gu;
  return !content ? '' : String(content)?.toUpperCase().replace(nonAlphaNumeric, '');
}

/**
 * Type guard: `value` is a string with at least one character.
 *
 * Uses lodash {@link https://lodash.com/docs#isString `isString`} and
 * {@link https://lodash.com/docs#isEmpty `isEmpty`}.
 * For plain strings, only `''` is empty; whitespace (e.g. `' '`) is still non-empty because its length is not zero.
 *
 * @param value - Any value to test.
 * @returns `true` when `value` is a non-empty string (TypeScript narrows accordingly).
 *
 * @example
 * isNonEmptyString('dock-1'); // true
 * @example
 * isNonEmptyString(''); // false
 * @example
 * isNonEmptyString('   '); // true — only length 0 counts as empty for strings
 * @example
 * isNonEmptyString(null); // false
 * @example
 * isNonEmptyString(0); // false — not a string
 */
export function isNonEmptyString(value: unknown): value is NonEmptyString {
  return isString(value) && !isEmpty(value);
}

/**
 * Returns `true` when value is `null`, `undefined`, or a string that is blank (empty after trimming).
 *
 * Complements {@link isNonEmptyString} for contexts where a whitespace-only string is also unusable.
 *
 * @param value - Any value to test.
 * @returns `true` when value is nil or a blank string.
 *
 * @example
 * isNilOrBlankString(null); // true
 * @example
 * isNilOrBlankString(undefined); // true
 * @example
 * isNilOrBlankString(''); // true
 * @example
 * isNilOrBlankString('   '); // true — blank after trim
 * @example
 * isNilOrBlankString('hello'); // false
 * @example
 * isNilOrBlankString(new Date()); // false — not nil, not a string
 */
export function isNilOrBlankString(value: unknown): boolean {
  return isNil(value) || (isString(value) && isEmpty(trim(value)));
}

const EMAIL_REGEX = /^[^\s@]+@(?<domain>[^\s@.]+(?:\.[^\s@.]+)+)$/u;

/**
 * Domain part of an email address;
 * empty string if the email has none or if the email is invalid.
 * Requires the domain to contain a dot.
 */
export function getEmailDomain(email: string): string {
  const match = email.trim().match(EMAIL_REGEX);
  return match?.groups?.domain ?? '';
}

/**
 * Simplified BCP 47-shaped language tag after {@link normalizeLanguageTag}
 * (language lowercase, 2-letter region uppercase).
 * Examples: `en`, `es-MX`, `pt-BR`.
 */
export const BCP47_LANGUAGE_TAG_REGEX = /^[a-z]{2,3}(?:-[A-Z]{2})?$/u;

export const BCP47_LANGUAGE_TAG_VALIDATION_MESSAGE =
  'must be a BCP 47 language tag (e.g. "en", "es-MX", "pt-BR")';

const LANGUAGE_REGEX = /^[a-z]{2,3}$/u;
const REGION_REGEX = /^[A-Z]{2}$/u;
const COMPACT_TAG_REGEX = /^[a-z]{4}$/u;

/** 2-letter codes used to split compact input such as `ptbr` → `pt-BR`. */
const COMPACT_LANGUAGE_CODES = new Set([
  'ar',
  'ca',
  'de',
  'en',
  'es',
  'fr',
  'hi',
  'it',
  'ja',
  'ko',
  'nl',
  'pl',
  'pt',
  'ru',
  'uk',
  'zh'
]);

function toLanguage(value: string): string | null {
  const language = value.toLowerCase();
  return LANGUAGE_REGEX.test(language) ? language : null;
}

function toRegion(value: string): string | null {
  const region = value.toUpperCase();
  return REGION_REGEX.test(region) ? region : null;
}

function tryCompactTag(value: string): string | null {
  const compact = value.toLowerCase();
  if (compact.length !== 4 || !COMPACT_TAG_REGEX.test(compact)) {
    return null;
  }

  const language = compact.slice(0, 2);
  const region = compact.slice(2);
  if (!COMPACT_LANGUAGE_CODES.has(language)) {
    return null;
  }

  const canonicalRegion = toRegion(region);
  return canonicalRegion ? `${language}-${canonicalRegion}` : null;
}

function tryHyphenatedTag(value: string): string | null {
  const parts = value.split('-').filter(Boolean);
  const language = toLanguage(parts[0] ?? '');
  if (!language) {
    return null;
  }

  if (parts.length === 1) {
    return language;
  }

  if (parts.length !== 2) {
    return null;
  }

  const region = toRegion(parts[1] ?? '');
  return region ? `${language}-${region}` : null;
}

/**
 * Normalizes language + optional region tags for persistence.
 * Examples: `pt-br`, `PT-BR`, `ptbr` → `pt-BR`; `en` → `en`.
 * Unrecognized or non-string values are returned unchanged (after trim for strings).
 */
export function normalizeLanguageTag(value: unknown): unknown {
  if (!isString(value)) {
    return value;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  const normalized = trimmed.replace(/_/gu, '-');

  if (!normalized.includes('-')) {
    return tryCompactTag(normalized) ?? toLanguage(normalized) ?? trimmed;
  }

  return tryHyphenatedTag(normalized) ?? trimmed;
}
